import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import './MyCourses.css';

const MyCourses = () => {
  const { user } = useAuth();
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [createdCourses, setCreatedCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  const isInstructor = user?.role === 'instructor' || user?.role === 'admin' || user?.role === 'sub-admin';

  useEffect(() => {
    const fetchMyCourses = async () => {
      try {
        // Fetch enrolled courses for all users
        const enrolledRes = await api.get('/enrollments/my-courses');
        setEnrolledCourses(enrolledRes.data);

        // For instructors, also fetch courses they created
        if (isInstructor) {
          const createdRes = await api.get('/courses/instructor/my-courses');
          setCreatedCourses(createdRes.data);
        }
      } catch (error) {
        console.error('Error fetching courses:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMyCourses();
  }, [isInstructor]);

  // Combined courses for display
  const courses = enrolledCourses;

  const handleUnenroll = async (courseId) => {
    if (!window.confirm('Are you sure you want to unenroll from this course?')) {
      return;
    }

    try {
      await api.delete(`/enrollments/${courseId}`);
      setEnrolledCourses(enrolledCourses.filter((c) => c.course_id !== courseId));
    } catch (error) {
      console.error('Error unenrolling:', error);
      alert('Failed to unenroll from course');
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  const completedCourses = courses.filter((course) => course.completed_at).length;
  const inProgressCourses = Math.max(courses.length - completedCourses, 0);

  return (
    <div className="page my-courses-page">
      <div className="container">
        <section className="my-courses-hero">
          <div className="hero-content">
            <p className="eyebrow">{isInstructor ? 'Instructor Dashboard' : 'Learning path'}</p>
            <h1>My Courses</h1>
            <p>{isInstructor
              ? 'Manage your created courses and track enrolled courses.'
              : 'Track progress, revisit completed material, or pick up where you left off.'}
            </p>
          </div>
          <div className="hero-stats">
            {isInstructor && (
              <div>
                <span className="label">Created</span>
                <strong>{createdCourses.length}</strong>
              </div>
            )}
            <div>
              <span className="label">Enrolled</span>
              <strong>{courses.length}</strong>
            </div>
            <div>
              <span className="label">In progress</span>
              <strong>{inProgressCourses}</strong>
            </div>
            <div>
              <span className="label">Completed</span>
              <strong>{completedCourses}</strong>
            </div>
          </div>
        </section>

        {/* Instructor: Show Created Courses Section */}
        {isInstructor && (
          <section className="created-courses-section">
            <div className="section-header">
              <h2>Courses You Created</h2>
              <Link to="/create-course" className="btn-primary">
                + Create New Course
              </Link>
            </div>
            {createdCourses.length === 0 ? (
              <div className="empty-state elevated">
                <p>You haven't created any courses yet.</p>
                <Link to="/create-course" className="btn-primary">
                  Create Your First Course
                </Link>
              </div>
            ) : (
              <div className="my-courses-grid">
                {createdCourses.map((course) => (
                  <article key={course.id} className="enrolled-card instructor-card">
                    <div className="card-top">
                      <span className="card-chip">{course.category_name || 'Course'}</span>
                      <span className={`status-pill ${course.status}`}>
                        {course.status === 'published' ? 'Published' : 'Draft'}
                      </span>
                    </div>
                    <h3>{course.title}</h3>
                    <p className="card-instructor">{course.enrollment_count || 0} students enrolled</p>
                    <div className="card-actions">
                      <Link to={`/courses/${course.id}`} className="btn-primary">
                        View Course
                      </Link>
                      <Link to={`/edit-course/${course.id}`} className="btn-secondary">
                        Edit
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Enrolled Courses Section */}
        {isInstructor && <h2 className="section-title">Enrolled Courses</h2>}

        {courses.length === 0 ? (
          <div className="empty-state elevated">
            <p>You haven't enrolled in any courses yet.</p>
            <Link to="/courses" className="btn-primary">
              Browse Courses
            </Link>
          </div>
        ) : (
          <div className="my-courses-grid">
            {courses.map((course) => {
              const progressPercentage =
                course.total_videos > 0
                  ? Math.round((course.completed_videos / course.total_videos) * 100)
                  : 0;
              const isCompleted = Boolean(course.completed_at);

              return (
                <article
                  key={course.id}
                  className={`enrolled-card ${isCompleted ? 'completed' : ''}`}
                >
                  <div className="card-top">
                    <span className="card-chip">
                      {course.category_name || 'Course'}
                    </span>
                    {isCompleted && <span className="status-pill">Completed</span>}
                  </div>

                  <h3>{course.title}</h3>
                  <p className="card-instructor">with {course.instructor_name}</p>

                  <div className="card-progress">
                    <div className="progress-track">
                      <div
                        className="progress-fill"
                        style={{ width: `${progressPercentage}%` }}
                      ></div>
                    </div>
                    <span className="progress-value">{progressPercentage}%</span>
                  </div>
                  <p className="progress-details">
                    {course.completed_videos} of {course.total_videos} lessons
                    completed
                  </p>

                  <p className="enrollment-date">
                    Enrolled {new Date(course.enrolled_at).toLocaleDateString()}
                  </p>

                  <div className="card-actions">
                    <Link
                      to={`/courses/${course.course_id}`}
                      className="btn-primary"
                    >
                      {isCompleted ? 'Review course' : 'Continue learning'}
                    </Link>
                    <button
                      onClick={() => handleUnenroll(course.course_id)}
                      className="btn-ghost danger"
                    >
                      Unenroll
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyCourses;
